import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { loadStripe } from '@stripe/stripe-js';

import {
  getCards,
  getEstimateDepositCheckoutSession,
  getEstimateDepositInfo,
  payEstimateDeposit,
} from 'api';

import config from 'config';

import { getApiErrorMessage } from 'helpers/error.helper';

import ButtonGroup from 'components/ui-kit/button-group';
import Button from 'components/ui-kit/buttons/button';
import Modal from 'components/ui-kit/modal';

import { useApp, useModal, useToast } from 'hooks';

import * as S from './styles';

const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;

const PayEstimateDepositModal = ({ id }) => {
  const { hideModal } = useModal();
  const { portal } = useApp();
  const portalId = portal?.id;
  const toast = useToast();
  const queryClient = useQueryClient();

  const [paymentMethodId, setPaymentMethodId] = useState('');

  const { data: depositRes } = useQuery(
    ['estimate-deposit', portalId, id],
    getEstimateDepositInfo,
    { enabled: !!portalId, refetchOnWindowFocus: false, retry: false },
  );
  const amount = depositRes?.data?.amount || 0;

  const { data: cardsRes } = useQuery(
    ['cardsListingQuery', portalId],
    getCards,
    { enabled: !!portalId, refetchOnWindowFocus: false },
  );
  const cards = cardsRes?.data || [];

  useEffect(() => {
    const firstCard = cardsRes?.data?.[0]?.methodId;
    if (firstCard) {
      setPaymentMethodId((prev) => prev || firstCard);
    }
  }, [cardsRes]);

  const payMutation = useMutation(() => payEstimateDeposit(portalId, id, paymentMethodId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['appointment', portalId, id]);
      queryClient.invalidateQueries(['estimate-deposit', portalId, id]);
    },
  });

  const noCard = !cards.length;

  const redirectToCheckout = async () => {
    const { data: session } = await getEstimateDepositCheckoutSession(portalId, id);
    const { sessionId, connectedAccountId } = session || {};

    if (!sessionId || !connectedAccountId) {
      toast.addErrorToast('Company did not connect bank account');
      return;
    }

    const stripe = await loadStripe(config.stripePublicKey, { stripeAccount: connectedAccountId });
    await stripe.redirectToCheckout({ sessionId });
  };

  const pay = async () => {
    try {
      if (noCard) {
        await redirectToCheckout();
        return;
      }

      await payMutation.mutateAsync();
      toast.addSuccessToast('Deposit paid');
      hideModal();
    } catch (error) {
      toast.addErrorToast(getApiErrorMessage(error, 'Deposit payment failed'));
      hideModal();
    }
  };

  return (
    <Modal size="4/10" title="Pay deposit">
      <S.Callout>
        <S.Row>
          <S.Label>Deposit due</S.Label>
          <S.Value>{formatMoney(amount)}</S.Value>
        </S.Row>
        {noCard ? (
          <S.Note>You’ll be redirected to a secure Stripe page to pay the deposit.</S.Note>
        ) : (
          <S.CardSelect
            value={paymentMethodId}
            onChange={(e) => setPaymentMethodId(e.target.value)}
          >
            {cards.map((card) => (
              <option key={card.methodId} value={card.methodId}>
                {`${card.brand || 'Card'} •••• ${card.last4}`}
              </option>
            ))}
          </S.CardSelect>
        )}
      </S.Callout>

      <ButtonGroup justifyContent="flex-end" topGap="1rem">
        <Button onClick={hideModal}>Cancel</Button>
        <Button
          color="success"
          onClick={pay}
          isLoading={payMutation.isLoading}
          disabled={(!noCard && !paymentMethodId) || payMutation.isLoading}
        >
          {`Pay ${formatMoney(amount)}`}
        </Button>
      </ButtonGroup>
    </Modal>
  );
};

PayEstimateDepositModal.propTypes = {
  id: PropTypes.string.isRequired,
};

export default PayEstimateDepositModal;
